'use client'

import { usePathname } from 'next/navigation'
import { ArrowRight, BookOpenText, ChevronRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { formatDateNormal, formatImageURL } from '@/utils/util'
import { getExcerpt, getReadingTime } from '@/utils/blog'
import { navigateUrl } from '@/utils/client-request'
import BlogCard from './BlogCard'

export default function BlogList({ blogs, vendor }: { blogs: any[], vendor?: any }) {
  const pathname = usePathname()
  const sorted = [...(blogs || [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  const vendorName = vendor?.name || 'Дэлгүүр'

  if (sorted.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 py-20 flex flex-col items-center justify-center gap-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
        <BookOpenText className="h-10 w-10" />
        <div className="text-[14px]">Нийтлэл олдсонгүй</div>
      </div>
    )
  }

  const [featured, ...rest] = sorted
  const featuredCategory = featured.category?.name || featured.category || ''
  const featuredReading = getReadingTime(featured.content)
  const featuredExcerpt = getExcerpt(featured.content, 180)

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10">
        <div className="hidden md:flex items-center gap-1.5 text-[12px] mb-8" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">Блог</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="mt-3 flex items-end justify-between gap-8 flex-wrap">
            <h1
              className="font-semibold tracking-[-0.025em] leading-[0.95] text-white max-w-3xl"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(40px, 7vw, 64px)' }}
            >
              Нийтлэл, зөвлөмжүүд
            </h1>
            <div className="hidden md:block text-right pb-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Нийт</div>
              <div
                className="mt-1 font-semibold tabular-nums text-white leading-none"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 36 }}
              >
                {sorted.length}
              </div>
              <div className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>нийтлэл</div>
            </div>
          </div>
          <p className="mt-5 text-[15px] leading-7 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Манай дэлгүүрийн шинэ мэдээлэл болон мэргэжлийн зөвлөмжүүдийг уншина уу
          </p>
        </motion.div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-10">
        <motion.a
          href={navigateUrl(`/blog/${featured.id}`, pathname)}
          className="group block relative rounded-3xl overflow-hidden aspect-21/9 cursor-pointer"
          style={{ border: '1px solid rgba(255,255,255,0.13)' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
          viewport={{ once: true }}
        >
          <img
            src={formatImageURL(featured.thumbnail || 'placeholder.png')}
            alt={featured.title}
            className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out will-change-transform group-hover:scale-105"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(110deg, rgba(0,0,0,0.75) 30%, rgba(0,0,0,0.1) 75%)' }} />
          <div className="relative h-full p-8 sm:p-14 flex flex-col justify-end max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
              <span className="px-2 py-0.5 rounded-full bg-white tabular-nums text-[10px]" style={{ color: '#0a0a0c' }}>FEATURED</span>
              {featuredCategory && <span>{featuredCategory}</span>}
            </div>
            <h2
              className="mt-4 font-semibold tracking-[-0.025em] leading-none text-white line-clamp-3"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 5vw, 56px)' }}
            >
              {featured.title}
            </h2>
            {featuredExcerpt && (
              <p className="mt-4 text-[15px] leading-7 max-w-xl line-clamp-3" style={{ color: 'rgba(255,255,255,0.8)' }}>
                {featuredExcerpt}
              </p>
            )}
            <div className="mt-6 flex items-center gap-4 text-[12px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
              <span className="tabular-nums">{formatDateNormal(new Date(featured.createdAt))}</span>
              {featuredReading > 0 && (
                <>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span className="tabular-nums">{featuredReading} мин унших</span>
                </>
              )}
            </div>
            <div className="mt-6">
              <span
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-[13px] font-medium backdrop-blur-md"
                style={{ background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.22)', color: 'white' }}
              >
                Бүрэн унших <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </div>
        </motion.a>
      </div>

      {rest.length > 0 && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
            {rest.map((blog, i) => <BlogCard key={blog.id} blog={blog} index={i} />)}
          </div>
        </div>
      )}
    </div>
  )
}
